import { ProtectedComponent } from '@/components/ProtectedComponent';
import { SortableHeader } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useUpdateUserRoleMutation } from '@/query/user.query';

import { Permissions } from '@/constants';

import { RowAction } from './row-action';

import type { Role } from '@/types/role.type';
import type { User } from '@/types/user.type';
import type { ColumnDef } from '@tanstack/react-table';

const RoleCell = ({ member, roles }: { member: User; roles: Role[] }) => {
  const updateUserRoleMutation = useUpdateUserRoleMutation();

  return (
    <ProtectedComponent
      requiredPermissions={[Permissions.UsersUpdate]}
      fallback={<span className="text-sm">{member.role.name}</span>}
    >
      <Select
        value={member.role.id.toString()}
        onValueChange={value => updateUserRoleMutation.mutate({ userId: member.id, role: value })}
      >
        <SelectTrigger className="w-50">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {roles.map(role => (
            <SelectItem key={role.id} value={role.id.toString()}>
              {role.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </ProtectedComponent>
  );
};

export const getColumns = (roles: Role[]): ColumnDef<User>[] => [
  {
    id: 'name',
    accessorKey: 'first_name',
    header: ({ column }) => <SortableHeader label="Name" column={column} />,
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.first_name} {row.original.last_name}
      </span>
    ),
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: ({ column }) => <SortableHeader label="Email" column={column} />,
    cell: ({ getValue }) => <span>{getValue() as string}</span>,
  },
  {
    id: 'role',
    header: 'Role',
    cell: ({ row }) => <RoleCell member={row.original} roles={roles} />,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader label="Status" column={column} />,
    cell: ({ getValue }) => <span>{getValue() as string}</span>,
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => <RowAction key={row.original.id} member={row.original} />,
  },
];
